import type { CodingColumnId } from 'app/util/columnVisibility';

/**
 * Option key for coding field selection menus.
 * Derived from CodingColumnId to maintain a single source of truth for column identifiers.
 */
export type CodingOptionKey = CodingColumnId;

/**
 * Display values for coding fields (used for readonly display)
 */
export interface CodingDisplayValues {
  glCode: string;
  projectCode: string;
  subJob: string;
  costCode: string;
  costType: string;
  vendor: string;
  location: string;
  department: string;
  subaccount: string;
  employee: string;
  billableItem: string;
  equipment: string;
  emCostCode: string;
  emCostType: string;
}

/**
 * Watched values for editable mode - maps option keys to their selected values
 */
export interface WatchedCodingValues {
  glCode?: unknown;
  project?: unknown;
  subJob?: unknown;
  costCode?: unknown;
  costType?: unknown;
  vendor?: unknown;
  location?: unknown;
  department?: unknown;
  subaccount?: unknown;
  employee?: unknown;
  billableItem?: unknown;
  equipment?: unknown;
  emCostCode?: unknown;
  emCostType?: unknown;
}

/**
 * Current coding values for editable mode - maps to transaction field values
 */
export interface CurrentCodingValues {
  glCode?: unknown;
  projectCode?: unknown;
  subJob?: unknown;
  costCode?: unknown;
  costType?: unknown;
  vendor?: unknown;
  location?: unknown;
  department?: unknown;
  subaccount?: unknown;
  employee?: unknown;
  billableItem?: unknown;
  equipment?: unknown;
  emCostCode?: unknown;
  emCostType?: unknown;
}

/**
 * Configuration for a single coding field
 */
export interface CodingFieldConfig {
  /** Column ID matching CodingColumnId type */
  id: CodingColumnId;
  /** Key in CodingDisplayValues object */
  valueKey: keyof CodingDisplayValues;
  /** i18n key for readonly label (details view) */
  readonlyLabelKey: string;
  /** Fallback for readonly label when i18n key is missing */
  defaultReadonlyLabel?: string;
  /** i18n key for editable title (edit view) */
  editableTitleKey: string;
  /** Fallback for editable title when i18n key is missing */
  defaultEditableTitle?: string;
  /** Option key for handleOpenOptions/handleClearOption (edit view) */
  optionKey: CodingOptionKey;
}

/**
 * Configuration for all coding fields.
 * Used by MobileCodingFieldList to render fields consistently.
 */
export const CODING_FIELD_CONFIGS: CodingFieldConfig[] = [
  {
    id: 'glCode',
    valueKey: 'glCode',
    readonlyLabelKey: 'mobileTransactionDetails.generalLedger',
    editableTitleKey: 'mobileTransactionEdit.selectGLAccount',
    optionKey: 'glCode',
  },
  {
    id: 'project',
    valueKey: 'projectCode',
    readonlyLabelKey: 'mobileTransactionDetails.project',
    editableTitleKey: 'mobileTransactionEdit.selectProject',
    optionKey: 'project',
  },
  {
    id: 'subJob',
    valueKey: 'subJob',
    readonlyLabelKey: 'transactionSidesheet.subJob',
    editableTitleKey: 'mobileTransactionEdit.selectSubJob',
    optionKey: 'subJob',
  },
  {
    id: 'costCode',
    valueKey: 'costCode',
    readonlyLabelKey: 'mobileTransactionDetails.costCode',
    editableTitleKey: 'mobileTransactionEdit.selectCostCode',
    optionKey: 'costCode',
  },
  {
    id: 'costType',
    valueKey: 'costType',
    readonlyLabelKey: 'mobileTransactionDetails.costType',
    editableTitleKey: 'mobileTransactionEdit.selectCostType',
    optionKey: 'costType',
  },
  {
    id: 'vendor',
    valueKey: 'vendor',
    readonlyLabelKey: 'mobileTransactionDetails.vendor',
    editableTitleKey: 'mobileTransactionEdit.selectVendor',
    optionKey: 'vendor',
  },
  {
    id: 'location',
    valueKey: 'location',
    readonlyLabelKey: 'mobileTransactionDetails.location',
    editableTitleKey: 'mobileTransactionEdit.selectLocation',
    optionKey: 'location',
  },
  {
    id: 'department',
    valueKey: 'department',
    readonlyLabelKey: 'mobileTransactionDetails.department',
    defaultReadonlyLabel: 'Department',
    editableTitleKey: 'mobileTransactionEdit.selectDepartment',
    defaultEditableTitle: 'Select Department',
    optionKey: 'department',
  },
  {
    id: 'subaccount',
    valueKey: 'subaccount',
    readonlyLabelKey: 'transactionSidesheet.subaccounts',
    defaultReadonlyLabel: 'Subaccount',
    editableTitleKey: 'mobileTransactionEdit.selectSubaccount',
    defaultEditableTitle: 'Select Subaccount',
    optionKey: 'subaccount',
  },
  {
    id: 'employee',
    valueKey: 'employee',
    readonlyLabelKey: 'mobileTransactionDetails.employee',
    defaultReadonlyLabel: 'Employee',
    editableTitleKey: 'mobileTransactionEdit.selectEmployee',
    defaultEditableTitle: 'Select Employee',
    optionKey: 'employee',
  },
  // SEE-1135 — per-transaction Sage Intacct billable item. Gated by isVisible()
  // via the column's requiresPaidolSetting (sageIntacctBillableSettings.isEnabled),
  // exactly like location/department gate on their settings.
  {
    id: 'billableItem',
    valueKey: 'billableItem',
    readonlyLabelKey: 'mobileTransactionDetails.billableItem',
    defaultReadonlyLabel: 'Item',
    editableTitleKey: 'mobileTransactionEdit.selectBillableItem',
    defaultEditableTitle: 'Select Item',
    optionKey: 'billableItem',
  },
  // SEE-1091 — Vista equipment coding. Filtered out unless the equipment flag is
  // on (isVisible() strips EQUIPMENT_CODING_COLUMN_IDS), so flag-off is unchanged.
  {
    id: 'equipment',
    valueKey: 'equipment',
    readonlyLabelKey: 'mobileTransactionDetails.equipment',
    defaultReadonlyLabel: 'Equipment',
    editableTitleKey: 'mobileTransactionEdit.selectEquipment',
    defaultEditableTitle: 'Select Equipment',
    optionKey: 'equipment',
  },
  {
    id: 'emCostCode',
    valueKey: 'emCostCode',
    readonlyLabelKey: 'mobileTransactionDetails.emCostCode',
    defaultReadonlyLabel: 'Equipment Cost Code',
    editableTitleKey: 'mobileTransactionEdit.selectEmCostCode',
    defaultEditableTitle: 'Select Equipment Cost Code',
    optionKey: 'emCostCode',
  },
  {
    id: 'emCostType',
    valueKey: 'emCostType',
    readonlyLabelKey: 'mobileTransactionDetails.emCostType',
    defaultReadonlyLabel: 'Equipment Cost Type',
    editableTitleKey: 'mobileTransactionEdit.selectEmCostType',
    defaultEditableTitle: 'Select Equipment Cost Type',
    optionKey: 'emCostType',
  },
];

/**
 * Maps option keys to watched value keys
 */
export const WATCHED_VALUE_KEYS: Partial<Record<CodingOptionKey, keyof WatchedCodingValues>> = {
  glCode: 'glCode',
  project: 'project',
  subJob: 'subJob',
  costCode: 'costCode',
  costType: 'costType',
  vendor: 'vendor',
  location: 'location',
  department: 'department',
  subaccount: 'subaccount',
  employee: 'employee',
  billableItem: 'billableItem',
  equipment: 'equipment',
  emCostCode: 'emCostCode',
  emCostType: 'emCostType',
};

/**
 * Maps option keys to current value keys
 */
export const CURRENT_VALUE_KEYS: Partial<Record<CodingOptionKey, keyof CurrentCodingValues>> = {
  glCode: 'glCode',
  project: 'projectCode',
  subJob: 'subJob',
  costCode: 'costCode',
  costType: 'costType',
  vendor: 'vendor',
  location: 'location',
  department: 'department',
  subaccount: 'subaccount',
  employee: 'employee',
  billableItem: 'billableItem',
  equipment: 'equipment',
  emCostCode: 'emCostCode',
  emCostType: 'emCostType',
};
